import { AUTH_TYPES, TEMPLATE_CATEGORIES } from './constants.js'

/*
 * PagerDuty Events API v2 alerts for privacy deadline escalation.
 *
 * POSTs to the fixed host https://events.pagerduty.com/v2/enqueue. The endpoints carry no url of their
 * own, so the wizard pre-fills the request URL from defaults.endpointAddress (the complete enqueue
 * URL). There is no runtime auth: the routing key (Events API v2 integration key) travels in the
 * request body and is admin-replaced ([ROUTING_KEY]); treat it as a revocable, single-purpose secret.
 * Tagged GENERIC so it attaches to any no-auth system integration. dedup_key = prp-<EventNumber>
 * absorbs redelivery: identical on a redelivered row, fresh on a re-armed event, distinct per task.
 */
export const pagerdutyTemplate = {
	id: 'pagerduty',
	guid: '5d707b55-1430-43c2-9497-b8f6cfdc2a58',
	name: 'PagerDuty',
	category: TEMPLATE_CATEGORIES.GENERIC,
	popularity: 'Popular',
	color: '#06ac38',
	icon: 'mdi-bell-alert',
	description: 'Trigger PagerDuty incidents when privacy requests approach or breach their statutory deadlines. Paste your Events API v2 routing key (treat it as a credential).',
	features: ['Events API v2', 'Deadline Escalation', 'Dedup Keys', 'No Auth Setup'],
	endpoints: [
		{
			name: 'Notify - Request/Task Nearing Deadline',
			method: 'POST',
			description: 'Trigger a PagerDuty warning alert when a privacy request or task enters its warning window.',
			cassieObjectID: -16,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'EventNumber', filter: '' },
				{ name: 'EventTitle', filter: '' },
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'DaysUntilDue', filter: '' }
			],
			requestBody: '{ "routing_key": "[ROUTING_KEY]", "event_action": "trigger", "dedup_key": "prp-[PLACEHOLDER_EVENTNUMBER]", "payload": { "summary": "[PLACEHOLDER_EVENTTITLE] - Request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]), due in [PLACEHOLDER_DAYSUNTILDUE] day(s)", "severity": "warning", "source": "Syrenis Privacy Rights" } }',
			headers: [
				{ Name: 'Content-Type', value: 'application/json' }
			],
			successResponses: [{ code: 202, message: '' }]
		},
		{
			name: 'Notify - Deadline Breached',
			method: 'POST',
			description: 'Trigger a critical PagerDuty alert when a privacy request or task passes its statutory deadline.',
			cassieObjectID: -17,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'EventNumber', filter: '' },
				{ name: 'EventTitle', filter: '' },
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'DaysOverdue', filter: '' }
			],
			requestBody: '{ "routing_key": "[ROUTING_KEY]", "event_action": "trigger", "dedup_key": "prp-[PLACEHOLDER_EVENTNUMBER]", "payload": { "summary": "[PLACEHOLDER_EVENTTITLE] - Request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]), [PLACEHOLDER_DAYSOVERDUE] day(s) overdue", "severity": "critical", "source": "Syrenis Privacy Rights" } }',
			headers: [
				{ Name: 'Content-Type', value: 'application/json' }
			],
			successResponses: [{ code: 202, message: '' }]
		}
	],
	defaults: {
		endpointAddress: 'https://events.pagerduty.com/v2/enqueue',
		authenticationTypeId: AUTH_TYPES.NO_AUTH,
		requestThrottlingWaitTimeMS: 1000,
		notes: 'PagerDuty Events API v2 alerts for privacy deadline escalation (nearing deadline maps to warning, breach maps to critical). No runtime auth: paste your Events API v2 routing key over [ROUTING_KEY] in the body and treat it as a revocable secret. The dedup_key (prp- prefixed with each event number) makes redelivery idempotent. Keep a non-zero throttle; the first sweep after deploy may burst a backlog of breached requests.'
	}
}
